import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { fetchPlan, type FetchTrigger } from '../../lib/fetchPlan';
import { useFetchTrigger, prefetchBypass } from '../../lib/useFetchTrigger';
import { sameWindow } from './domain';
import type { Booking, DateWindow } from './types';

// Fetches bookings OVERLAPPING a date window from GET /api/bookings/calendar —
// the date-windowed endpoint (BUG-CHARTS-WINDOW). Calendar, charts, and the
// stat strip are date-relevant views and must read this, not the recency-capped
// list. Refetches whenever the window moves (month nav). 401 → login, handled
// once inside the client. A failed window fetch resolves to an empty set rather
// than tearing down the whole page — the table (its own fetch) still renders;
// the cancelled guard (component lifecycle) stays here.
//
// keep-alive-page-host (AC3/AC4): this fetch feeds the Bookings stat strip. On a
// return to the page it revalidates silently — the current set holds (the strip
// keeps its numbers, no blank), the refetch bypasses the cache to catch a change
// made elsewhere, and the fresh data swaps in place. A failed revalidation
// leaves the last-known set untouched (does NOT blank to empty), so a transient
// error on return never wipes the strip.
//
// keep-alive-page-host (AC7): a same-page write must move the strip in place,
// with no nav. The hook exposes reload(); BookingsView calls it from
// onModalSaved (and any same-page commit) so the user's own create / status
// change refreshes the strip at once. Like the on-return revalidation, a reload
// bypasses the cache (catch the write) and holds the current numbers across the
// fetch (no flash) — same shape as useBookings / useOwnersPage.

export interface WindowedFetchPlan {
  // Whether to bypass the browser cache for this fetch.
  bypassCache: boolean;
  // Whether to hold the current numbers on screen across the fetch rather than
  // risk a blank. True for any background refresh that has content to keep — an
  // on-return revalidation (AC4) or a same-page-write reload (AC7).
  silent: boolean;
}

// Pure: decide how the windowed fetch behaves for a given trigger. The windowed
// hook has no loading skeleton of its own (it only holds the array), so "silent"
// here means "hold the last set, never fall back to empty mid-fetch" — applied
// to any cache-bypassing background refresh that already has content. bypassCache
// is the shared fetchPlan decision (a reload or a revalidation both bypass; a
// plain window move re-reads cacheable). Exported for unit testing.
export function windowedFetchPlan(t: FetchTrigger): WindowedFetchPlan {
  const plan = fetchPlan(t);
  return {
    bypassCache: plan.bypassCache,
    // Hold content across any cache-bypassing background refresh (reload or
    // revalidation) that has something on screen to keep — no flash.
    silent: plan.bypassCache && t.hasContent,
  };
}

export interface UseWindowedBookings {
  // The bookings for the window currently SHOWN — held across a window change so
  // the view never blanks (hold-until-ready).
  bookings: Booking[];
  // The window the `bookings` are actually for — NULL until a first response
  // has landed. During a window change this lags the requested range until the
  // new data arrives, so the consumer renders the old window's grid (no blank),
  // then both swap atomically.
  window: DateWindow | null;
  // Nothing loaded yet — the consumer shows a skeleton (the only time it blanks).
  loading: boolean;
  // A different window is loading while the current one is still shown — the
  // consumer can dim to acknowledge the click without a flash.
  isPending: boolean;
  // The last attempt for this window FAILED. Only consumers that draw a
  // conclusion from emptiness need this: an empty `bookings` after a failed
  // first load means "never loaded", not "nothing there". A grid can ignore it.
  failed: boolean;
  // Re-fetch the shown window now, bypassing the cache (AC7 same-page write).
  reload: () => void;
}

// Windowed bookings with seamless transitions. On a window change it HOLDS the
// last-loaded window + data on screen and only swaps once the new window's data
// arrives (hold-until-ready) — never a blank-then-populate flash. The first load
// (nothing to hold) reports `loading` so the consumer shows a skeleton. Optional
// `prefetch` windows are cache-warmed once settled, so stepping to an adjacent
// month/range is instant. The standard for any in-page windowed view.
export function useWindowedBookings(
  range: DateWindow,
  prefetch: DateWindow[] = [],
): UseWindowedBookings {
  // The window + data on screen. Held until the next window's data is ready.
  // `synthetic` marks a set that was never actually read — the empty fallback a
  // failed FIRST load leaves behind so the view has something to render. It is
  // stamped with the requested window, which made it indistinguishable from a
  // window that genuinely holds no bookings, and two behaviours keyed off that
  // stamp: the failure flag is scoped to its window, so stepping away cleared
  // it, and the fetch effect skips a window it is already showing, so stepping
  // back never retried. A date could then say "Nothing to chase…" for ever
  // about bookings it had never once read (PR #166, codex P1 + claude P2).
  const [shown, setShown] = useState<{ window: DateWindow; bookings: Booking[]; synthetic?: boolean } | null>(null);
  const [isPending, setIsPending] = useState(false);
  // The window whose attempt FAILED, not a bare flag. A boolean is only ever
  // cleared by a later success, so a week stepped to after a failure wore the
  // verdict from the moment it was requested until its own answer arrived — a
  // conclusion about data nobody had asked for yet (PR #165, codex P2).
  // useShifts and useHolidays scope theirs the same way; this hook was the one
  // the architecture paper described and did not match.
  const [failedFor, setFailedFor] = useState<DateWindow | null>(null);
  // The shared SWR trigger core: the reload nonce (AC7 same-page write) is
  // distinct from the activation/mutation tick (AC4 on-return revalidation), so
  // each fetch knows its trigger. hasContent is derived from `shown` (the
  // hold-until-ready state), not the core's monotonic tracking.
  const trigger = useFetchTrigger();
  // The latest shown window/data, read inside the effect without making it a dep
  // (so the effect only re-runs on a genuine window/trigger change).
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    const cur = shownRef.current;
    const run = trigger.begin({ hasContent: cur !== null });
    // A SYNTHETIC SET IS NOT "already showing this window" — it is a placeholder
    // standing where an answer should be, and the whole point is to go and get
    // one.
    const isCurrentWindow = sameWindow(cur?.window, range) && cur?.synthetic !== true;
    // Already showing this exact window and not a forced refresh → nothing to do.
    if (isCurrentWindow && !run.isRevalidate && !run.isReload) return;

    setIsPending(true);
    const plan = windowedFetchPlan(run);
    const params = new URLSearchParams({ from: range.from, to: range.to });
    api<Booking[]>(`/bookings/calendar?${params.toString()}`, plan.bypassCache ? { cache: 'reload' } : {})
      .then((body) => {
        if (!run.isCurrent()) return;
        // Atomic swap — window + data change together, so the grid and its
        // bookings are always consistent (no new-month-with-old-data flash).
        setShown({ window: range, bookings: body.data ?? [] });
        setIsPending(false);
        setFailedFor(null);
      })
      .catch(() => {
        if (!run.isCurrent()) return;
        setIsPending(false);
        setFailedFor(range);
        // Hold the last-shown window on failure. Only the very first load (with
        // nothing to hold) falls back to an empty set so the view can render.
        //
        // THAT FALLBACK IS INDISTINGUISHABLE FROM A GENUINELY EMPTY WINDOW, and
        // a consumer that draws a CONCLUSION from "no bookings" — "nothing to
        // chase" — would state it as fact when the window never loaded (PR #157,
        // Codex P1). `failed` is how such a consumer tells the two apart; the
        // grid consumers, which only draw what they are handed, ignore it.
        if (shownRef.current === null) setShown({ window: range, bookings: [], synthetic: true });
      });
    return run.cancel;
  }, [range.from, range.to, trigger.tick, trigger.nonce]);

  // Prefetch (cache-warm) adjacent windows once the current one has settled, so
  // a step to a neighbour resolves from cache instantly.
  //
  // A WRITE MUST RE-WARM THEM, NOT JUST THE SHOWN ONE. The mutation path
  // bypasses the cache for the window on screen; the neighbours were left as
  // ordinary cache entries, so a user who edited a booking and then stepped one
  // day was served the pre-write answer as though freshly loaded —
  // stale-while-revalidate stretching the window further still (PR #158, Codex
  // P2). The prefetch now re-runs on the same trigger as the main fetch and
  // bypasses the cache whenever that fetch would.
  const prefetchKey = prefetch.map((w) => `${w.from}|${w.to}`).join(',');
  const prefetchSeen = useRef({ nonce: trigger.nonce, tick: trigger.tick });
  useEffect(() => {
    if (isPending) return;
    // Only the run a write or an activation CAUSED bypasses the cache. The
    // counters are monotonic, so "either is non-zero" was true for the hook's
    // whole life after the first write, and every later window move
    // re-fetched warm neighbours from the Worker (codex, PR #176).
    const bypass = prefetchBypass(prefetchSeen.current, trigger);
    prefetchSeen.current = { nonce: trigger.nonce, tick: trigger.tick };
    for (const w of prefetch) {
      api<Booking[]>(`/bookings/calendar?from=${w.from}&to=${w.to}`, bypass ? { cache: 'reload' } : {}).catch(
        () => {},
      );
    }
    // prefetchKey captures the window set; `prefetch` identity is not stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetchKey, isPending, trigger.tick, trigger.nonce]);

  return {
    bookings: shown?.bookings ?? [],
    // NULL UNTIL SOMETHING HAS LOADED. This used to report the REQUESTED range
    // before the first response, which told a window-comparing consumer that an
    // empty placeholder was settled data for the window it asked about — the
    // false empty state this field exists to prevent (PR #165, codex P2).
    window: shown?.window ?? null,
    loading: shown === null,
    isPending,
    // Failed for THIS window, or holding a set for it that was never read. The
    // second half matters after a step away and back: the flag belongs to the
    // attempt, the placeholder belongs to the window, and only the placeholder
    // survives the round trip.
    failed:
      sameWindow(failedFor, range) ||
      (sameWindow(shown?.window, range) && shown?.synthetic === true),
    reload: trigger.reload,
  };
}
